import { useState } from 'react';
import { Sparkles, Loader2, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';

/**
 * Advisory AI feedback panel. Collapsible, never blocks progression.
 *
 * Props:
 *   refining: boolean
 *   feedback: { refined, suggestion, flags } | null
 *   onRefine: () => void  — triggers an AI refine call
 *   disabled: boolean
 *   labels: { refine, refining, advisory }
 *   onAccept?: (refined: string) => void — optional, inserts refined back into field
 */
export default function AIFeedbackPanel({
  refining,
  feedback,
  onRefine,
  disabled,
  labels,
  onAccept,
}) {
  const [open, setOpen] = useState(true);
  const hasFeedback = Boolean(feedback && (feedback.refined || feedback.suggestion || (feedback.flags && feedback.flags.length)));

  return (
    <div className="mt-3 border border-[#e3e3e3] dark:border-white/10 rounded-lg overflow-hidden bg-[#faf8fb] dark:bg-white/[0.03]">
      <div className="flex items-center justify-between px-4 py-2.5">
        <button
          type="button"
          onClick={onRefine}
          disabled={disabled || refining}
          className="inline-flex items-center gap-2 text-sm font-medium text-[#98139C] dark:text-[#d87bdb] hover:underline disabled:opacity-50"
        >
          {refining ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          {refining ? labels.refining : labels.refine}
        </button>
        {hasFeedback && (
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-label={open ? 'Collapse AI panel' : 'Expand AI panel'}
            className="text-[#30302F] dark:text-white/70 hover:text-[#98139C] dark:hover:text-[#d87bdb]"
          >
            {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        )}
      </div>

      {hasFeedback && open && (
        <div className="px-4 pb-4 space-y-3 border-t border-[#e3e3e3] dark:border-white/10">
          <p className="text-[11px] uppercase tracking-wide text-[#30302F]/60 dark:text-white/50 pt-3">
            {labels.advisory}
          </p>

          {feedback.refined && (
            <div>
              <div className="flex items-start justify-between gap-3">
                <p className="text-sm text-[#30302F] dark:text-white whitespace-pre-wrap leading-relaxed">
                  {feedback.refined}
                </p>
                {onAccept && (
                  <button
                    type="button"
                    onClick={() => onAccept(feedback.refined)}
                    className="shrink-0 text-[11px] font-semibold uppercase text-[#98139C] dark:text-[#d87bdb] hover:underline"
                  >
                    Use this
                  </button>
                )}
              </div>
            </div>
          )}

          {feedback.suggestion && (
            <p className="text-sm italic text-[#30302F]/80 dark:text-white/80">
              {feedback.suggestion}
            </p>
          )}

          {feedback.flags && feedback.flags.length > 0 && (
            <div className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-300">
              <AlertCircle size={14} className="mt-0.5 shrink-0" />
              <div className="flex flex-wrap gap-1">
                {feedback.flags.map((f) => (
                  <span key={f} className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 rounded text-[11px]">
                    {f.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
